import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../config/api'
import { fetchCsrfToken, getCsrfToken, clearCsrfToken } from '../lib/csrf'
import {
  ArrowPathIcon, MagnifyingGlassIcon, UsersIcon, EnvelopeIcon, BuildingOffice2Icon,
  CalendarDaysIcon, GlobeAltIcon, ChevronRightIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { AdminShell } from '../components/admin/AdminShell'
import { Card, PillButton, Avatar, Chip, StatusTag, EmptyState, SectionHead, Skeleton, Modal } from '../components/dashboard/primitives'
import { FolderStatCard } from '../components/dashboard/FolderStatCard'
import { PageHead, HeaderStats } from '../components/dashboard/Layout'
import { greetingFor } from '../components/dashboard/greeting'

// ─── Types ──────────────────────────────────────────────────

interface VerificationStats {
  total: number
  verified: number
  failed: number
  pending: number
  manual_review: number
}

interface WebhookStats {
  total_webhooks: number
  active_webhooks: number
  total_deliveries: number
  successful_deliveries: number
  failed_deliveries: number
  pending_deliveries: number
}

interface Developer {
  id: string
  email: string
  full_name: string | null
  company_name: string | null
  is_verified: boolean
  created_at: string
  webhook_url: string | null
  verification_stats: VerificationStats
  webhook_stats: WebhookStats
}

type VerifiedFilter = 'all' | 'verified' | 'unverified'
type SortKey = 'newest' | 'oldest' | 'most_active' | 'name'

// ─── Helpers ────────────────────────────────────────────────

const formatDate = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const authFetchOpts = (): RequestInit => ({ credentials: 'include' })

function DeveloperDetail({ dev, onClose }: { dev: Developer; onClose: () => void }) {
  const s = dev.verification_stats
  const rows: Array<[string, string]> = [
    ['Developer ID', dev.id],
    ['Email', dev.email],
    ['Name', dev.full_name || '—'],
    ['Company', dev.company_name || '—'],
    ['Email verified', dev.is_verified ? 'Yes' : 'No'],
    ['Webhook URL', dev.webhook_url || 'None'],
    ['Webhooks', `${dev.webhook_stats?.active_webhooks ?? 0} active / ${dev.webhook_stats?.total_webhooks ?? 0} total`],
    ['Deliveries', `${dev.webhook_stats?.successful_deliveries ?? 0} delivered · ${dev.webhook_stats?.failed_deliveries ?? 0} failed`],
    ['Signed up', formatDate(dev.created_at)],
  ]
  return (
    <Modal title={dev.full_name || dev.email} sub={dev.company_name || dev.email} onClose={onClose} width={560}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 8, marginBottom: 18 }}>
        {[
          { l: 'Total', v: s?.total ?? 0 }, { l: 'Verified', v: s?.verified ?? 0 }, { l: 'Failed', v: s?.failed ?? 0 },
          { l: 'Review', v: s?.manual_review ?? 0 }, { l: 'Pending', v: s?.pending ?? 0 },
        ].map((x, i) => (
          <div key={x.l} className={`sf-card ${i === 1 ? 'accent' : 'muted'}`} style={{ padding: '12px 10px', borderRadius: 18, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 600 }} className="sf-num">{x.v.toLocaleString()}</div>
            <div style={{ fontSize: 11, opacity: 0.75, textTransform: 'uppercase', letterSpacing: '.06em' }}>{x.l}</div>
          </div>
        ))}
      </div>
      <div className="sf-list">
        {rows.map(([k, v]) => (
          <div className="sf-row" key={k} style={{ padding: '9px 0' }}>
            <span style={{ width: 130, color: 'var(--sf-mid)', fontSize: 13, flexShrink: 0 }}>{k}</span>
            <span style={{ fontSize: 13.5, wordBreak: 'break-all', fontWeight: 500 }} className="sf-num">{v}</span>
          </div>
        ))}
      </div>
    </Modal>
  )
}

// ─── Component ──────────────────────────────────────────────

export function DevelopersList() {
  const navigate = useNavigate()

  // ── State ──
  const [developers, setDevelopers] = useState<Developer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [verifiedFilter, setVerifiedFilter] = useState<VerifiedFilter>('all')
  const [sort, setSort] = useState<SortKey>('newest')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [selected, setSelected] = useState<Developer | null>(null)
  const [authReady, setAuthReady] = useState(() => !!getCsrfToken())

  const LIMIT = 24

  // ── Auth guard (cookie-only, no escalation) ──
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/admin/developers?limit=1`, { credentials: 'include' })
      .then(res => {
        if (res.ok) { setAuthReady(true); fetchCsrfToken(); return }
        clearCsrfToken()
        // Not a platform admin — redirect to verifications or login
        if (res.status === 401) navigate('/admin/login')
        else navigate('/admin/verifications')
      })
      .catch(() => { clearCsrfToken(); navigate('/admin/login') })
  }, [navigate])

  // ── Fetch developers ──
  const fetchDevelopers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) })
      const res = await fetch(`${API_BASE_URL}/api/admin/developers?${params}`, authFetchOpts())
      if (res.status === 401) { navigate('/admin/login'); return }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setDevelopers(data.developers || [])
      setTotalPages(data.pagination?.pages || 1)
      setTotalCount(data.pagination?.total || 0)
    } catch (err: any) {
      setError(err.message || 'Failed to load developers')
    } finally {
      setLoading(false)
    }
  }, [page, navigate])

  useEffect(() => { if (authReady) fetchDevelopers() }, [authReady, fetchDevelopers])

  // ── Derived stats (current page) ──
  const verifiedCount = developers.filter(d => d.is_verified).length
  const unverifiedCount = developers.filter(d => !d.is_verified).length
  const activeCount = developers.filter(d => (d.webhook_stats?.total_webhooks ?? 0) > 0 || d.verification_stats?.total > 0).length
  const totalVerifications = developers.reduce((a, d) => a + (d.verification_stats?.total ?? 0), 0)
  const newest = developers.reduce<Developer | null>((a, d) => (!a || d.created_at > a.created_at ? d : a), null)

  // ── Search / filter / sort (client-side on loaded page) ──
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase()
    const list = developers.filter(d =>
      (!q || (d.full_name || '').toLowerCase().includes(q) || d.email.toLowerCase().includes(q) || (d.company_name || '').toLowerCase().includes(q)) &&
      (verifiedFilter === 'all' || (verifiedFilter === 'verified' ? d.is_verified : !d.is_verified)),
    )
    const sorted = [...list]
    if (sort === 'oldest') sorted.sort((a, b) => a.created_at.localeCompare(b.created_at))
    else if (sort === 'most_active') sorted.sort((a, b) => (b.verification_stats?.total ?? 0) - (a.verification_stats?.total ?? 0))
    else if (sort === 'name') sorted.sort((a, b) => (a.full_name || a.email).localeCompare(b.full_name || b.email))
    else sorted.sort((a, b) => b.created_at.localeCompare(a.created_at))
    return sorted
  }, [developers, searchQuery, verifiedFilter, sort])

  return (
    <AdminShell role="platform">
      <div className="sf-container">
        <PageHead
          title={`${greetingFor()}!`}
          sub="Every organisation registered on Saflens and how they're using it."
          actions={<>
            <HeaderStats items={[
              { label: 'Organisations', value: totalCount.toLocaleString() },
              { label: 'Verified', value: verifiedCount.toLocaleString() },
              { label: 'Active', value: activeCount.toLocaleString() },
            ]} />
            <PillButton variant="primary" icon={<ArrowPathIcon />} onClick={fetchDevelopers}>Refresh</PillButton>
          </>}
        />

        <form className="sf-filter" onSubmit={e => { e.preventDefault(); setSearchQuery(searchInput.trim()) }}>
          <div className="sf-filter-col" style={{ flex: 1.6 }}>
            <label htmlFor="dev-search">Search</label>
            <input id="dev-search" placeholder="Name, email or company" value={searchInput} onChange={e => { setSearchInput(e.target.value); if (!e.target.value) setSearchQuery('') }} />
          </div>
          <div className="sf-filter-col">
            <label htmlFor="dev-verified">Email status</label>
            <select id="dev-verified" value={verifiedFilter} onChange={e => setVerifiedFilter(e.target.value as VerifiedFilter)}>
              <option value="all">All organisations</option>
              <option value="verified">Verified email</option>
              <option value="unverified">Unverified email</option>
            </select>
          </div>
          <div className="sf-filter-col">
            <label htmlFor="dev-sort">Sort by</label>
            <select id="dev-sort" value={sort} onChange={e => setSort(e.target.value as SortKey)}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="most_active">Most verifications</option>
              <option value="name">Name A–Z</option>
            </select>
          </div>
          <button type="submit" className="sf-filter-go" aria-label="Search organisations"><MagnifyingGlassIcon /></button>
        </form>

        <div className="sf-grid c4" style={{ marginBottom: 30 }}>
          <FolderStatCard label="Organisations" tone="accent" loading={loading} value={totalCount.toLocaleString()} caption="On this page" captionValue={`${developers.length} loaded`} />
          <FolderStatCard label="Verified email" loading={loading} value={verifiedCount.toLocaleString()} caption="Unverified" captionValue={unverifiedCount.toLocaleString()} />
          <FolderStatCard label="Verifications" loading={loading} value={totalVerifications.toLocaleString()} caption="Across this page" captionValue={`${activeCount} active orgs`} />
          <FolderStatCard label="Newest" loading={loading}
            visual={newest ? <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}><Avatar name={newest.full_name || newest.email} size={48} /><div style={{ minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{newest.full_name || newest.email}</div><div style={{ color: 'var(--sf-mid)', fontSize: 13 }}>{newest.company_name || 'No company'}</div></div></div> : <span style={{ color: 'var(--sf-mid)' }}>No sign-ups yet</span>}
            caption="Signed up" captionValue={newest ? formatDate(newest.created_at) : '—'} />
        </div>

        <SectionHead
          title="Organisations"
          count={filtered.length}
          action={totalPages > 1 ? <span style={{ color: 'var(--sf-mid)', fontSize: 13 }}>Page {page} of {totalPages}</span> : undefined}
        />

        {loading ? (
          <div className="sf-grid c3">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} h={260} r={28} />)}</div>
        ) : error ? (
          <Card><EmptyState icon={<ExclamationTriangleIcon />} title="Couldn't load organisations" action={<PillButton size="sm" variant="dark" onClick={fetchDevelopers}>Retry</PillButton>}>{error}</EmptyState></Card>
        ) : filtered.length === 0 ? (
          <Card><EmptyState icon={<UsersIcon />} title={searchQuery || verifiedFilter !== 'all' ? 'No organisations match' : 'No organisations yet'}>{searchQuery || verifiedFilter !== 'all' ? 'Try a different search or filter.' : 'Developers appear here as soon as they sign up.'}</EmptyState></Card>
        ) : (
          <div className="sf-grid c3">
            {filtered.map(dev => {
              const s = dev.verification_stats
              const tags = [
                s?.verified ? `${s.verified.toLocaleString()} verified` : null,
                s?.manual_review ? `${s.manual_review.toLocaleString()} in review` : null,
                s?.failed ? `${s.failed.toLocaleString()} failed` : null,
                s?.pending ? `${s.pending} pending` : null,
                dev.webhook_stats?.active_webhooks ? `${dev.webhook_stats.active_webhooks} webhook${dev.webhook_stats.active_webhooks > 1 ? 's' : ''}` : null,
              ].filter(Boolean) as string[]
              const shown = tags.slice(0, 2)
              return (
                <Card key={dev.id} className="sf-lcard sf-fade">
                  <div className="sf-lcard-top">
                    <Avatar name={dev.full_name || dev.email} size={48} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="sf-lcard-name">{dev.full_name || dev.email.split('@')[0]}</div>
                      <div className="sf-lcard-sub">{dev.company_name || 'Independent developer'}</div>
                    </div>
                    {dev.is_verified ? <StatusTag tone="ok">Verified</StatusTag> : <StatusTag tone="warn">Unverified</StatusTag>}
                  </div>
                  <div className="sf-lcard-meta">
                    <div><EnvelopeIcon /><span>{dev.email}</span></div>
                    <div><CalendarDaysIcon /><span>Joined {formatDate(dev.created_at)}</span></div>
                    <div>{dev.webhook_url ? <GlobeAltIcon /> : <BuildingOffice2Icon />}<span>{dev.webhook_url || `${dev.webhook_stats?.total_deliveries ?? 0} webhook deliveries`}</span></div>
                  </div>
                  <div className="sf-chips">
                    {shown.length ? shown.map(t => <Chip key={t}>{t}</Chip>) : <Chip>No activity yet</Chip>}
                    {tags.length > shown.length && <Chip tone="outline">+{tags.length - shown.length}</Chip>}
                  </div>
                  <div className="sf-lcard-foot">
                    <div className="sf-lcard-metric"><b>{(s?.total ?? 0).toLocaleString()}</b><span>verifications</span></div>
                    <PillButton variant="dark" size="sm" iconRight={<ChevronRightIcon />} onClick={() => setSelected(dev)}>View details</PillButton>
                  </div>
                </Card>
              )
            })}
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 26 }}>
            <PillButton variant="grey" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</PillButton>
            <span className="sf-num" style={{ color: 'var(--sf-mid)', fontSize: 13 }}>{page} / {totalPages}</span>
            <PillButton variant="grey" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</PillButton>
          </div>
        )}
      </div>
      {selected && <DeveloperDetail dev={selected} onClose={() => setSelected(null)} />}
    </AdminShell>
  )
}
